// #556 — the Polymarket order-ATTEMPT producer for the ticket's result line.
//
// Sibling of `describeOscarSequenceResult`: same job, different venue path —
// turn one authoritative venue outcome into an `OrderResultLine` the shared
// `OrderResultBanner` can render.
//
// Why its own module rather than beside the Oscar producer: this directory
// consistently gives a small pure helper its own named file plus a matching
// test (`orderEntryEconomics`, `orderEntryPayloads`, `orderEntrySubmitGate`,
// `polyOrderTokens`), and `oscarResultPresentation` is what `OrderResultBanner`
// imports — reaching `describePolyOrderOutcome` from there would put the relay
// client (and through it `apiFetch` and the auth store) in the banner's import
// graph. To be precise about the strength of that: it is a layering
// preference, not a fix for a demonstrated failure — the alternative was tried
// and both of those suites still pass. The convention is the real argument.
//
// This module is PASSIVE. The relay already decided, from the observed
// submission phase, whether the CLOB post had begun (#549) — this only
// translates that decision into a tone. It reads the stable code and nothing
// else: never the message text, and never a judgement of its own about how
// harmless the failure looks.

import { describePolyOrderOutcome, type PolyOrderOutcomeCode } from '@/lib/polymarket/relay';
import type { OrderResultLine, OrderResultSeverity } from './oscarResultPresentation';

/**
 * Toned by EXPOSURE — the rule #474 settled for this slot. `critical` means
 * the trader may be holding something and has to act; `warning` means the
 * attempt failed with the book untouched, so the only cost is a retry.
 *
 * The two pre-submit codes are emitted ONLY when the post had not begun, so
 * "your book is untouched" is proven rather than assumed. That proof is the
 * whole reason they may leave the red fallback: a bare `{ ok: false }` cannot
 * say whether the order reached the venue, which is why plain placements still
 * fall back to red (see `resultSeverity`).
 *
 * `POLY_ORDER_STATE_UNKNOWN` stays critical for the inverse reason. The post
 * HAD begun, an order may be resting right now, and the copy sends the trader
 * to Open Orders. Amber would undercut the one instruction that matters — it
 * is the failure #549 exists to prevent, wearing a different coat.
 *
 * Exhaustive by type: dropping a code fails `tsc`, and the suite pins every
 * value, so widening `PolyOrderOutcomeCode` cannot silently inherit a tone.
 */
const SEVERITY_BY_OUTCOME: Record<PolyOrderOutcomeCode, OrderResultSeverity> = {
  POLY_MARKET_METADATA_UNAVAILABLE: 'warning',
  POLY_ORDER_PREP_FAILED: 'warning',
  POLY_ORDER_STATE_UNKNOWN: 'critical',
};

/**
 * The whole line for one typed Polymarket order-attempt outcome. Copy and tone
 * are chosen from the SAME code in the SAME call, so the two cannot drift into
 * an amber banner carrying an "ORDER UNCONFIRMED" sentence.
 */
export function describePolyOrderOutcomeLine(code: PolyOrderOutcomeCode): OrderResultLine {
  return {
    ok: false,
    msg: describePolyOrderOutcome(code),
    severity: SEVERITY_BY_OUTCOME[code],
  };
}
